import { handleMutationErrorsWithForm } from '@tinycld/core/lib/errors'
import { mutation, useMutation } from '@tinycld/core/lib/mutations'
import { useStore } from '@tinycld/core/lib/pocketbase'
import { ButtonIcon, ButtonText, ServerActionButton } from '@tinycld/core/ui/button'
import {
    FormErrorSummary,
    SelectInput,
    TextInput,
    useForm,
    z,
    zodResolver,
} from '@tinycld/core/ui/form'
import { Plus } from 'lucide-react-native'
import { newRecordId } from 'pbtsdb/core'
import { Text, View } from 'react-native'
import { type MailboxType, newMailboxRecords } from './mailbox-records'

const mailboxSchema = z.object({
    address: z
        .string()
        .min(1, 'Address is required')
        .max(64)
        .regex(/^[a-z0-9._-]+$/, 'Only lowercase letters, numbers, dots, hyphens, underscores'),
    domain: z.string().min(1, 'Domain is required'),
    display_name: z.string().min(1, 'Display name is required').max(200),
})

export type MailboxFormValues = z.infer<typeof mailboxSchema>

interface CreateProps {
    mode: 'create'
    domainOptions: Array<{ label: string; value: string }>
    userId: string
    onDone: () => void
    /** Default 'shared'. 'personal' makes the creator's own address. */
    type?: MailboxType
    defaults?: Partial<Pick<MailboxFormValues, 'address' | 'display_name'>>
}

interface EditProps {
    mode: 'edit'
    mailboxId: string
    initial: MailboxFormValues
    domainName: string
    onDone: () => void
}

type Props = CreateProps | EditProps

export function MailboxForm(props: Props) {
    return props.mode === 'create' ? <CreateForm {...props} /> : <EditForm {...props} />
}

function CreateForm({ domainOptions, userId, onDone, type = 'shared', defaults }: CreateProps) {
    const [mailboxesCollection, membersCollection] = useStore(
        'mail_mailboxes',
        'mail_mailbox_members'
    )

    const {
        control,
        handleSubmit,
        setError,
        getValues,
        watch,
        reset,
        formState: { errors, isSubmitted, isValid },
    } = useForm<MailboxFormValues>({
        mode: 'onChange',
        resolver: zodResolver(mailboxSchema),
        defaultValues: {
            address: defaults?.address ?? '',
            domain: domainOptions[0]?.value ?? '',
            display_name: defaults?.display_name ?? '',
        },
    })

    const createMutation = useMutation({
        mutationFn: mutation(function* (data: MailboxFormValues) {
            const { mailbox, member } = newMailboxRecords({
                data,
                type,
                userId,
                mailboxId: newRecordId(),
                memberId: newRecordId(),
            })
            yield mailboxesCollection.insert(mailbox)
            yield membersCollection.insert(member)
        }),
        onSuccess: () => {
            reset()
            onDone()
        },
        onError: handleMutationErrorsWithForm({ setError, getValues }),
    })

    const onSubmit = handleSubmit(d => createMutation.mutate(d))
    // Not gated on isDirty: prefilled defaults are a valid submission as-is.
    const canSubmit = !createMutation.isPending && isValid
    const values = watch()
    const domainName = domainOptions.find(d => d.value === values.domain)?.label ?? ''

    return (
        <FormLayout
            errors={errors}
            isSubmitted={isSubmitted}
            control={control}
            domainOptions={domainOptions}
            preview={{
                address: values.address,
                domainName,
                displayName: values.display_name,
            }}
            submitLabel={createMutation.isPending ? 'Creating…' : 'Create inbox'}
            canSubmit={canSubmit}
            onSubmit={onSubmit}
        />
    )
}

function EditForm({ mailboxId, initial, domainName, onDone }: EditProps) {
    const [mailboxesCollection] = useStore('mail_mailboxes')

    const {
        control,
        handleSubmit,
        setError,
        getValues,
        watch,
        formState: { errors, isSubmitted, isDirty, isValid },
    } = useForm<MailboxFormValues>({
        mode: 'onChange',
        resolver: zodResolver(mailboxSchema),
        defaultValues: initial,
    })

    const editMutation = useMutation({
        mutationFn: mutation(function* (data: MailboxFormValues) {
            yield mailboxesCollection.update(mailboxId, draft => {
                draft.address = data.address
                draft.display_name = data.display_name
                draft.name = data.display_name
            })
        }),
        onSuccess: onDone,
        onError: handleMutationErrorsWithForm({ setError, getValues }),
    })

    const onSubmit = handleSubmit(d => editMutation.mutate(d))
    const canSubmit = !editMutation.isPending && isDirty && isValid
    const values = watch()

    return (
        <FormLayout
            errors={errors}
            isSubmitted={isSubmitted}
            control={control}
            domainOptions={[{ label: domainName, value: initial.domain }]}
            domainDisabled
            preview={{
                address: values.address,
                domainName,
                displayName: values.display_name,
            }}
            submitLabel={editMutation.isPending ? 'Saving…' : 'Save changes'}
            canSubmit={canSubmit}
            onSubmit={onSubmit}
        />
    )
}

interface FormLayoutProps {
    errors: Parameters<typeof FormErrorSummary>[0]['errors']
    isSubmitted: boolean
    control: ReturnType<typeof useForm<MailboxFormValues>>['control']
    domainOptions: Array<{ label: string; value: string }>
    domainDisabled?: boolean
    preview: { address: string; domainName: string; displayName: string }
    submitLabel: string
    canSubmit: boolean
    onSubmit: () => void
}

function FormLayout({
    errors,
    isSubmitted,
    control,
    domainOptions,
    domainDisabled,
    preview,
    submitLabel,
    canSubmit,
    onSubmit,
}: FormLayoutProps) {
    const previewInitial = (preview.displayName || preview.address || '?')
        .trim()
        .charAt(0)
        .toUpperCase()

    return (
        <View className="gap-4">
            <FormErrorSummary
                errors={errors}
                isEnabled={isSubmitted}
                testID="mailbox-form-errors"
            />
            <TextInput
                control={control}
                name="address"
                label="Address"
                placeholder="support"
                hint={`will be: ${preview.address || '…'}@${preview.domainName || '…'}`}
            />
            {domainOptions.length > 1 && !domainDisabled ? (
                <SelectInput
                    control={control}
                    name="domain"
                    label="Domain"
                    options={domainOptions}
                />
            ) : null}
            <TextInput
                control={control}
                name="display_name"
                label="Display name"
                placeholder="Support Team"
                hint="shown in the From: header and in the admin list"
            />
            <View className="flex-row gap-3 items-center rounded-lg p-3 border border-border">
                <View
                    className="items-center justify-center rounded-lg bg-primary/10"
                    style={{ width: 30, height: 30 }}
                >
                    <Text className="text-primary" style={{ fontWeight: '700', fontSize: 13 }}>
                        {previewInitial}
                    </Text>
                </View>
                <View className="flex-1" style={{ minWidth: 0 }}>
                    <Text className="text-foreground" style={{ fontSize: 13, fontWeight: '600' }}>
                        {preview.address || '…'}
                        <Text className="text-muted-foreground" style={{ fontWeight: '500' }}>
                            @{preview.domainName || '…'}
                        </Text>
                    </Text>
                    <Text className="text-muted-foreground" style={{ fontSize: 11.5 }}>
                        {preview.displayName || 'Display name'}
                    </Text>
                </View>
                <Text
                    className="text-muted-foreground"
                    style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        fontWeight: '600',
                        letterSpacing: 0.6,
                    }}
                >
                    Preview
                </Text>
            </View>
            <ServerActionButton
                testID="mailbox-form-submit"
                className="h-11"
                onPress={onSubmit}
                isDisabled={!canSubmit}
            >
                <ButtonIcon as={Plus} />
                <ButtonText>{submitLabel}</ButtonText>
            </ServerActionButton>
        </View>
    )
}
